import React, { useState, useEffect, useContext, } from "react";
import {
    mdiAntenna, mdiDownload, mdiLayers, mdiTapeMeasure,
    mdiVectorSquarePlus, mdiImageFilterDramaOutline,
    mdiImageFilterDrama, mdiVideo2d, mdiRotate3d
} from "@mdi/js";
import styled from "styled-components";
import intl from "react-intl-universal";
import { useLocation } from "wouter";

import { Plot } from "../../../../script/plot";
import { EntryContext } from "../../../contexts/EntryContext";

import { OriginLayer } from "./layers/OriginLayer";
import { Button } from "../../../components/Button";
import { MeasureLayer } from "./layers/MeasureLayer";
import { Tooltip } from "../../../components/Tooltip";
import { AreasLayer } from "./layers/area/AreasLayer";
import { PlansLayer } from "./layers/plan/PlansLayer";
import { AntennasLayer } from "./layers/AntennasLayer";
import { DownloadLayer } from "./layers/DownloadLayer";
import { OverviewLayer } from "./layers/overview/OverviewLayer";
import { WorstcaseLayer } from "./layers/wostcase/WorstcaseLayer";
import { OverviewLayer3d } from "./layers/overview3d/Overview3dLayer";
import { WattwaechterLayer } from "./layers/wattwaechter/WattwaechterLayer";

const TabStyle = styled.div`
    padding: 20px 0 0 0;
    border-radius: 0px 0px 5px 5px;
    border-top: 3px solid ${props => props.theme.greyLight};
`;

const ContentStyle = styled.div`
    gap: 10px;
    display: flex;
    overflow: hidden;
    height: calc(100vh - 245px);
`;

const LeftStyle = styled.div`
    display: flex;
    width: 100%;
    min-width: 500px;
    overflow: hidden;
    justify-content: center;
`;

const MenuStyle = styled.div`
    position: absolute;
    z-index: 10;
    display: flex;
    gap: 6px;
    padding: 0 8px 6px 8px;
    border: 1px solid ${props => props.theme.greyLight};
    border-top: none;
    border-radius: 0 0 2px 2px;
    background-color: ${props => props.theme.greyLightest};
`;

const CanvasStyle = styled.div`
    display: ${props => (props.$visible ? "flex" : "none")};
    flex-direction: column;
    overflow: hidden;
    width: 100%;
    border-radius: 2px;
    border: 1px solid ${props => props.theme.greyLight};
    #plan-canvas-wrapper {
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow: hidden;
        background-color: ${props => props.theme.white};
    }
    #plan-legend {
        position: relative;
        font-size: 14px;
    }
`;

const RightStyle = styled.div`
    min-width: 430px;
    max-width: 430px;
    overflow: hidden;
    padding: 0 1px 0 1px;
    >div {
        height: 100%;
        flex-direction: column;
    }
`;

export const PlanTab = props => {
    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const layers = Object.freeze({
        PLANS: "plans",
        ANTENNAS: "antenna",
        AREA: "area",
        WATTWAECHTER: "wattwaechter",
        WORSTCASE: "worstcase",
        OVERVIEW: "overview",
        OVERVIEW_3D: "overview3d",
        MEASURE: "measure",
        DOWNLOAD: "download"
    });

    const [, setLocation] = useLocation();
    const [plot, setPlot] = useState();
    const [layer, setLayer] = useState(props.param2 || layers.PLANS);
    const [wizard, setWizard] = useState(false);

    useEffect(() => {
        const p = new Plot("plan-canvas", entry.id);
        setPlot(p);
    }, []);

    useEffect(() => {
        if (entry.id && plot) {
            plot.id = entry.id;
        }
    }, [entry.id]);

    const onChangeLayer = layer => {
        setLocation("/entry/" + entry.id + "/plan/" + layer);
        setLayer(layer);
    };

    useEffect(() => {
        if (location.pathname === `/entry/${entry.id}/plan`) {
            setLocation(`/entry/${entry.id}/plan/${layer}`);
        }
    }, [location.pathname, entry.id, layer]);


    const hasResults = () => entry.calcSetting.calcAreas.some(
        calcArea => calcArea.calculations && calcArea.calculations.length > 0
    );

    return (
        <TabStyle>
            <ContentStyle>
                <LeftStyle>
                    {plot && !wizard &&
                        <MenuStyle>
                            <Tooltip message={intl.get("entry.plan.plans.title")}>
                                <Button
                                    svgIcon={mdiLayers}
                                    disabled={layer === layers.PLANS}
                                    onClick={() => onChangeLayer(layers.PLANS)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.antennas.title")}>
                                <Button
                                    svgIcon={mdiAntenna}
                                    disabled={layer === layers.ANTENNAS}
                                    onClick={() => onChangeLayer(layers.ANTENNAS)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.areas.title")}>
                                <Button
                                    svgIcon={mdiVectorSquarePlus}
                                    disabled={layer === layers.AREA}
                                    onClick={() => onChangeLayer(layers.AREA)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.wattwaechter.title")}>
                                <Button
                                    svgIcon={mdiImageFilterDramaOutline}
                                    disabled={layer === layers.WATTWAECHTER}
                                    onClick={() => onChangeLayer(layers.WATTWAECHTER)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.worstcase.title")}>
                                <Button
                                    svgIcon={mdiImageFilterDrama}
                                    disabled={layer === layers.WORSTCASE}
                                    onClick={() => onChangeLayer(layers.WORSTCASE)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.overview.title")}>
                                <Button
                                    svgIcon={mdiVideo2d}
                                    disabled={layer === layers.OVERVIEW}
                                    onClick={() => onChangeLayer(layers.OVERVIEW)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.overview3d.title")}>
                                <Button
                                    svgIcon={mdiRotate3d}
                                    disabled={layer === layers.OVERVIEW_3D}
                                    onClick={() => onChangeLayer(layers.OVERVIEW_3D)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.measure.title")}>
                                <Button
                                    svgIcon={mdiTapeMeasure}
                                    disabled={layer === layers.MEASURE}
                                    onClick={() => onChangeLayer(layers.MEASURE)} />
                            </Tooltip>
                            <Tooltip message={intl.get("entry.plan.download.title")}>
                                <Button
                                    svgIcon={mdiDownload}
                                    disabled={layer === layers.DOWNLOAD}
                                    onClick={() => onChangeLayer(layers.DOWNLOAD)} />
                            </Tooltip>
                        </MenuStyle>
                    }
                    <CanvasStyle id="canvas-style" $visible={layer !== layers.OVERVIEW_3D}>
                        <div id="plan-canvas-wrapper">
                            <canvas id="plan-canvas" />
                        </div>
                        <div id="plan-legend" />
                    </CanvasStyle>
                    <CanvasStyle id="plot-3d" $visible={layer === layers.OVERVIEW_3D}>
                        <div id="gizmo-container" />
                    </CanvasStyle>
                </LeftStyle>
                <RightStyle>
                    {plot &&
                        <>
                            <PlansLayer plot={plot} show={layer === layers.PLANS} setWizard={setWizard} wizard={wizard} />
                            <AntennasLayer plot={plot} show={layer === layers.ANTENNAS} />
                            <AreasLayer plot={plot} show={layer === layers.AREA} />
                            {hasResults() &&
                                <>
                                    <WattwaechterLayer plot={plot} show={layer === layers.WATTWAECHTER} />
                                    <WorstcaseLayer plot={plot} show={layer === layers.WORSTCASE} />
                                    <OverviewLayer plot={plot} show={layer === layers.OVERVIEW} />
                                </>
                            }
                            {layer === layers.OVERVIEW_3D &&
                                <OverviewLayer3d show={layer === layers.OVERVIEW_3D} />
                            }
                            <MeasureLayer plot={plot} show={layer === layers.MEASURE} />
                            <DownloadLayer plot={plot} show={layer === layers.DOWNLOAD} />
                            <OriginLayer plot={plot} />
                        </>
                    }
                </RightStyle>
            </ContentStyle>
        </TabStyle >
    );
};
